import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  asyncActions,
  serialActions,
  consultationReasonsByNameActions,
} from '@docavenue/core';
import { FieldProps } from 'formik';
import {
  CardHcd,
  ConsultationReason,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { useRouter } from 'next/router';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import AvailabilityCalendar from '../Availability/AvailabilityCalendar';
import { temporaryAppointmentsActions } from '../../../src/actions';

import { useAsyncError } from '../../../src/hooks';
import { Trans, useTranslation } from '@/src/i18n';
import { isNoPractitionerSelected } from '../../utils/card';
import useIsPrescriber from '@/src/hooks/useIsPrescriber';
import InstructionsDialog from '@/components/organisms/InstructionsDialog/InstructionsDialog';
import { noNeedPaymentCardPractitionerSelector } from '@/src/selectors';
import {
  Instruction,
  InstructionSection,
  TLC_INSTRUCTION,
} from '@/components/utils/instruction';
import useInstructions from '@/src/hooks/useInstructions';
import { TELECONSULTATION } from '@/src/constants';

type Props = {
  card: CardHcd;
} & FieldProps;

const AvailabilitySelect = ({
  field,
  form: { setFieldValue, values, submitForm },
  card,
}: Props) => {
  const isPrescriber = useIsPrescriber();
  const throwError = useAsyncError();
  const { t } = useTranslation();
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();
  const consultationReason = useSelector(
    (state: any) => state?.consultationReasonsByName?.item,
  );

  const { data: instructions } = useInstructions({
    consultationReasonId: consultationReason?.id,
  });

  const availabilityCalendarRef = React.useRef<any>(null);

  const onAvailabilityCalendarClick = React.useCallback(
    slot => {
      setFieldValue(field.name, slot);
      const noPractitionerSelected = isNoPractitionerSelected(
        values.practitionerId,
      );
      if (
        !noPractitionerSelected &&
        (!instructions || instructions?.length === 0) &&
        values.type !== TELECONSULTATION
      ) {
        // setTimeout is important for delay submitForm after setFieldValue
        setTimeout(submitForm);
        return;
      }
      if (noPractitionerSelected) {
        const { practitionerId: pratId } = slot;
        const action = serialActions.serial([
          state => {
            const practitionerObj = state.practitioners.items.find(
              item => item.professional.practitionerId === pratId,
            );
            if (!practitionerObj) {
              throw new Error(`Not found practitioner ${pratId}`);
            }
            return temporaryAppointmentsActions.mergeItem({
              cardHcd: { ...practitionerObj },
              practitioner: { ...practitionerObj },
              slot,
            });
          },
          () => {
            if (
              (!instructions || instructions.length === 0) &&
              values.type !== TELECONSULTATION
            ) {
              // setTimeout is important for delay submitForm after setFieldValue
              setTimeout(submitForm);
            } else {
              setOpen(true);
            }
          },
        ]);

        action.onError = ({ error }) => throwError(error);
        dispatch(action);
      } else {
        setOpen(true);
      }
    },
    [instructions, values],
  );
  const onConsentFormSubmit = React.useCallback(() => {
    submitForm();
    setOpen(false);
  }, []);

  // concatenate two instructions: that received from request By Consultation ReasonId and instructions With Patient Limitation
  const combinedInstructions: Instruction[] =
    instructions
      ?.map(instruction => ({
        ...instruction,
        instruction: instruction.instruction || '',
      }))
      .concat(
        consultationReason?.patientLimitation &&
          consultationReason?.patientLimitation !== 'PUBLIC'
          ? {
              id: `instruction_patient_limitation_${consultationReason.patientLimitation.toLowerCase()}`,
              instruction: t(
                `instruction_patient_limitation_${consultationReason.patientLimitation.toLowerCase()}`,
              ),
            }
          : [],
      ) || [];

  const noNeedPayment = useSelector(noNeedPaymentCardPractitionerSelector);

  const instructionSections: InstructionSection[] = [
    {
      instructionItems: combinedInstructions,
    },
    ...(values?.type === TELECONSULTATION
      ? [
          {
            sectionTitle: noNeedPayment
              ? 'tlc-instruction'
              : 'tlc-instruction_plural',
            instructionItems: [
              ...(noNeedPayment
                ? []
                : [
                    {
                      id: 'tlc_consent_field_0_label',
                      instruction: 'tlc_consent_field_0_label',
                      shouldTranslate: true,
                    },
                  ]),
              TLC_INSTRUCTION,
            ],
          },
        ]
      : []),
  ];

  const {
    consultationReasonName,
    centerId,
    practitionerId,
    specialityId,
  } = values;

  useEffect(() => {
    let isCancelled = false;
    if (!(consultationReasonName && centerId)) return;
    const fetchConsulationReasonByName = async () => {
      const defaultParams = {
        consultationReasonName,
        rootCenterId: centerId,
        practitionerId,
      };
      const noPratParams = {
        consultationReasonName,
        rootCenterId: centerId,
        specialityId,
      };
      const params = isNoPractitionerSelected(practitionerId)
        ? noPratParams
        : defaultParams;
      const data = (await asyncActions(
        dispatch,
        consultationReasonsByNameActions
          .getActions(isPrescriber)
          .getList(params),
      )) as { items: ConsultationReason[] };
      if (isCancelled) return;
      dispatch(
        consultationReasonsByNameActions
          .getActions(isPrescriber)
          .setItems(data.items),
      );
      const consultationReasonItem = data.items.find(
        ({ name }) => name === consultationReasonName,
      );
      if (!consultationReasonItem) return;
      dispatch(
        consultationReasonsByNameActions
          .getActions(isPrescriber)
          .setItem(consultationReasonItem),
      );
    };
    fetchConsulationReasonByName();
    return () => {
      isCancelled = true;
    };
  }, [
    consultationReasonName,
    centerId,
    practitionerId,
    specialityId,
    dispatch,
  ]);

  React.useEffect(() => {
    if (availabilityCalendarRef.current && router.query.focus) {
      availabilityCalendarRef.current.backToCurrentDate(router.query.focus);
    }
  }, []);
  return (
    <>
      <AvailabilityCalendar
        isFirstAvailabilities
        card={card}
        centerId={centerId}
        practitionerId={values?.practitionerId}
        consultationReasonId={consultationReason?.id}
        consultationReasonName={consultationReasonName}
        consultationType={values?.type}
        settings={card?.settings}
        onClick={onAvailabilityCalendarClick}
        specialityId={specialityId}
        ref={availabilityCalendarRef}
      />
      <InstructionsDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={onConsentFormSubmit}
        topElement={
          combinedInstructions.length ? (
            <Box mb={2}>
              <Trans
                i18nKey="consent_form_reason_section"
                components={{
                  highlight: <Typography component="span" variant="h6" />,
                  reason: consultationReasonName,
                }}
              />
            </Box>
          ) : (
            undefined
          )
        }
        instructionSections={instructionSections}
      />
    </>
  );
};

export default AvailabilitySelect;
